import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';

import { GeneratedInviteLink, useInviteClientStore } from '@/features/invite-client';

function title(clientName: string) {
  return clientName ? `${clientName} is ready to invite` : 'Invite link created';
}

function description(hasPrefillPlan: boolean) {
  return hasPrefillPlan
    ? 'Their quick plan is ready to walk through, and the link below is yours to send.'
    : 'Send the link below to your client yourself.';
}

function InviteLinkCreatedContent() {
  const { createdInvite, dismissCreatedInvite } = useInviteClientStore();

  if (!createdInvite) return null;

  return (
    <div className="space-y-4">
      <GeneratedInviteLink link={createdInvite.link} />
      <p className="text-muted-foreground text-sm">
        The link works only for this client, and they will sign in with their email before
        starting the assessment.
      </p>
      <Button
        className="w-full"
        onClick={dismissCreatedInvite}
      >
        Done
      </Button>
    </div>
  );
}

/**
 * Shown after the invite dialog closes, so the advisor gets the link on a screen that is
 * clearly about the client they just created rather than a form primed to create another.
 */
export function InviteLinkCreatedModal() {
  const { createdInvite, dismissCreatedInvite } = useInviteClientStore();
  const isMobile = useIsMobile();

  const isOpen = !!createdInvite;
  const onOpenChange = (open: boolean) => {
    if (!open) dismissCreatedInvite();
  };

  if (isMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="z-60 max-h-[90vh]! overflow-y-auto px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title(createdInvite?.clientName ?? '')}</DrawerTitle>
            <DrawerDescription>
              {description(!!createdInvite?.hasPrefillPlan)}
            </DrawerDescription>
          </DrawerHeader>
          <InviteLinkCreatedContent />
          <DrawerFooter />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        // The link is the only copy the advisor gets from this screen, so a stray click
        // outside must not take it away.
        onInteractOutside={event => event.preventDefault()}
        className="z-60 max-h-[90vh] overflow-y-auto sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>{title(createdInvite?.clientName ?? '')}</DialogTitle>
          <DialogDescription>{description(!!createdInvite?.hasPrefillPlan)}</DialogDescription>
        </DialogHeader>
        <InviteLinkCreatedContent />
      </DialogContent>
    </Dialog>
  );
}
